import React, { useMemo, useState, useEffect } from "react";

const isClosed = (v) => String(v).toLowerCase() === "true";

const getEmployeeDay = (dayObj, employeeId) => {
  const sched = dayObj?.schedule;
  if (!sched || typeof sched !== "object") return null;
  const idStr = String(employeeId ?? "").trim();
  if (!idStr) return null;
  if (sched[idStr]) return sched[idStr];
  const matchKey = Object.keys(sched).find((k) => String(k).trim() === idStr);
  if (matchKey) return sched[matchKey];
  const keys = Object.keys(sched);
  if (keys.length === 1) return sched[keys[0]];
  return null;
};

function buildTimeOptions(dayObj, employeeId) {
  const day = getEmployeeDay(dayObj, employeeId);
  if (!day || isClosed(day.isClosed) || !Array.isArray(day.slots)) return [];
  return day.slots.map((s, i) => ({
    value: `${s.open} - ${s.close}`,
    label: `${s.open} - ${s.close}`,
    patients: s.patients ?? 0,
    key: i,
  }));
}

const PAGE_SIZE = 3;

export function SchedulePicker({
  scheduleList,
  employeeId,
  employee,
  employeeName,
  onConfirm,
  onClose,
}) {
  const showTime = employee?.type === 1 && employee?.empType === 3;

  const availableDates = useMemo(
    () =>
      (scheduleList || []).filter((d) => {
        const day = getEmployeeDay(d, employeeId);
        if (!day || isClosed(day.isClosed)) return false;
        if (!Array.isArray(day.slots) || day.slots.length === 0) return false;
        return true;
      }),
    [scheduleList, employeeId]
  );

  const [page, setPage] = useState(0);
  const [selectedDateObj, setSelectedDateObj] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");

  useEffect(() => {
    if (!selectedDateObj && availableDates.length > 0) {
      setSelectedDateObj(availableDates[0]);
    }
  }, [availableDates]);

  const timeOptions = useMemo(
    () => (selectedDateObj ? buildTimeOptions(selectedDateObj, employeeId) : []),
    [selectedDateObj, employeeId]
  );

  // ✅ No auto-select useEffect here — user must pick manually

  const handleDateSelect = (d) => {
    setSelectedDateObj(d);
    setSelectedTime(""); // reset on date change

    if (!showTime) {
      const daySlots = getEmployeeDay(d, employeeId)?.slots || [];
      const firstSlot = daySlots[0];
      const time = firstSlot ? `${firstSlot.open} - ${firstSlot.close}` : "";
      onConfirm({ date: d.date, time });
      onClose?.();
    }
  };

  const handleTimeSelect = (value) => {
    setSelectedTime(value);
    if (selectedDateObj?.date) {
      onConfirm({ date: selectedDateObj.date, time: value });
      onClose?.();
    }
  };

  const visibleDates = availableDates.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE
  );
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < availableDates.length;

  return (
    <div className="schedule-picker-toast">
      <p className="schedule-picker-toast__header">
        Select date{showTime && " & time"}
        {employeeName && <strong> for {employeeName}</strong>}
      </p>

      {availableDates.length === 0 ? (
        <div className="schedule-picker-toast__empty">
          <p className="text-center text-danger">No available schedule found for this employee.</p>
          
        </div>
      ) : (
        <div className="mt-3">

          {/* Date strip */}
          <div className="d-flex gap-2 mb-2">
            <button
              type="button"
              className="btn btn-primary text-secondary p-0 bg-transparent border-0"
              disabled={!canPrev}
              onClick={() => canPrev && setPage((p) => p - 1)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="bevel">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className="col">
              <div className="row">
                {visibleDates.map((d) => {
                  const isActive = selectedDateObj?.date === d.date;
                  const daySlots = getEmployeeDay(d, employeeId)?.slots || [];
                  const totalSlots = daySlots.reduce((sum, s) => sum + (s.patients ?? 0), 0);
                  return (
                    <div
                      key={d.date}
                      className={`col-4 px-1 text-center ${isActive ? "border-bottom border-3 border-primary" : ""}`}
                      onClick={() => handleDateSelect(d)}
                      style={{ cursor: "pointer" }}
                    >
                      <h6>{d.label}</h6>
                      {daySlots.length > 0 && (
                        <p className="small mb-1 text-success">{totalSlots} slots left</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary text-secondary p-0 bg-transparent border-0"
              disabled={!canNext}
              onClick={() => canNext && setPage((p) => p + 1)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="bevel">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* Time slots */}
          {showTime && (
            <div className="row">
              <ul className="customradio input-group two-grid mb-4 mt-2 px-3 mx-0">
                {timeOptions.length === 0 ? (
                  <li className="text-muted small">No slots available</li>
                ) : (
                  timeOptions.map((opt) => (
                    <li key={opt.key}>
                      <input
                        type="radio"
                        id={`slot-${opt.key}`}
                        name="bookingTime"
                        value={opt.value}
                        checked={selectedTime === opt.value}
                        onChange={() => handleTimeSelect(opt.value)}
                      />
                      <label htmlFor={`slot-${opt.key}`}>{opt.label}</label>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}

        </div>
      )}
    </div>
  );
}